import { MediaItem, formatFileSize } from '../../types/sound-management-types';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { Button } from '@/core/components/ui/button';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Trash2, AlertTriangle, FileCode, ExternalLink, Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '@/core/lib/utils';

async function downloadFile(url: string, filename: string) {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

interface WeezmoGridProps {
  files: MediaItem[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onDelete: (item: MediaItem) => void;
  onRetry: () => void;
}

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export function WeezmoGrid({
  files,
  isLoading,
  error,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onDelete,
  onRetry,
}: WeezmoGridProps) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? currentPage * pageSize + 1 : 0;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;
    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  if (isLoading) {
    return (
      <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
        <table className="w-full">
          <thead className={GRID_STYLES.header.background}>
            <tr>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>File Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>URL</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Size</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Uploaded</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>By</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i} className={cn(GRID_STYLES.row.default, `border-b ${GRID_STYLES.border}`)}>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-40" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-64" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-16" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-24" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-24" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-8 w-8" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-8 text-center">
        <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-4 text-lg font-medium text-red-800">Failed to load files</h3>
        <p className="mt-2 text-sm text-red-600">{error}</p>
        <Button onClick={onRetry} variant="outline" className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="rounded-md border border-[#E8E8E8] bg-gray-50 p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
          <FileCode className="h-6 w-6 text-gray-400" />
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No Weezmo JS files found</h3>
        <p className="mt-2 text-sm text-gray-500">Upload .js files to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
        <table className="w-full">
          <thead className={GRID_STYLES.header.background}>
            <tr>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>File Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>URL</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Size</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Uploaded</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>By</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((item) => (
              <tr
                key={item.mediaId}
                className={cn(GRID_STYLES.row.default, GRID_STYLES.row.hover, `border-b ${GRID_STYLES.border}`, 'transition-colors')}
              >
                <td className={GRID_STYLES.cell.base}>
                  <div className="flex items-center gap-2">
                    <FileCode className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate font-mono text-sm" title={item.originalFileName}>
                      {item.originalFileName}
                    </span>
                  </div>
                </td>
                <td className={GRID_STYLES.cell.base}>
                  <a
                    href={item.s3Url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline break-all"
                  >
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    {item.s3Url}
                  </a>
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600')}>
                  {formatFileSize(item.fileSizeBytes)}
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600')}>
                  {formatDate(item.created)}
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600')}>
                  {item.createdByName ?? '—'}
                </td>
                <td className={GRID_STYLES.cell.base}>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadFile(item.s3Url, item.originalFileName)}
                      className="h-8 w-8 p-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      title="Download file"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(item)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete file"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalCount > pageSize && (
        <div className={cn('flex items-center justify-between px-2 py-3 border-x border-b bg-white', GRID_STYLES.border)}>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(0)} disabled={currentPage === 0}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getPageNumbers().map((pageNum) => (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'default' : 'ghost'}
                size="sm"
                className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]' : 'text-[#262626] hover:bg-[#FFFBEB]'}`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum + 1}
              </Button>
            ))}
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
            <span className="text-sm text-[#606060] ml-2">{currentPage + 1} of {totalPages} pages</span>
          </div>
          <span className="text-sm text-[#606060]">{startItem}-{endItem} of {totalCount.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}
